import { useState } from 'react'
import { Button, Divider, Radio, theme } from 'antd'
import { useField } from 'react-final-form'
import { useLocations } from 'api'
import { ScreenSize, Location, ServiceLocation } from 'types'
import { useTranslate, useWindowDimensions } from 'utils'
import { HorizontalContainer, Label, VerticalContainer } from 'components/styled'
import { FormSection } from 'components/form-section/form-section'
import { VerticalPaginatedList } from 'components/vertical-paginated-list/vertical-paginated-list'
import { Avatar } from 'components/avatar/avatar'

type ServiceChangeLocationProps = {
    currentLocation: ServiceLocation | null
    onChange: (location: ServiceLocation | null) => void
    onClose: () => void
}

export const ServiceChangeLocation = ({ currentLocation, onChange, onClose }: ServiceChangeLocationProps) => {
    const { token: { padding, paddingMD, marginXS }} = theme.useToken()
    const { __ } = useTranslate()
    const { LG, SM, height } = useWindowDimensions()

    const [selectedLocation, setSelectedLocation] = useState<ServiceLocation | null>(currentLocation)
    const [search, setSearch] = useState<string | null>(null)

    const { input: { value: locationsValue } } = useField('locations')
    const currentSelectedLocations: ServiceLocation[] = locationsValue

    const {
        data: allLocations,
        pageNum,
        totalPages,
        isLoading,
        isPreviousData,
        isNextDisabled,
        isPreviousDisabled,
        fetchNext,
        fetchPrevious
    } = useLocations(search, 5) // 5 is to match the existing location select cache

    const onSelectLocation = (location: Location) => setSelectedLocation({
        ...location,
        resources: currentLocation?.resources || []
    })

    return (
        <FormSection
            title={__`change_location`}
            searchInput={{
                placeholder: __`search_locations`,
                onSearch: setSearch
            }}
            style={{
                border: 'none',
                boxShadow: 'none',
                padding: SM ? `${paddingMD}px 0` : padding
            }}
        >
            <VerticalContainer style={{ gap: marginXS }}>
                <div style={{ height: LG && height >= ScreenSize.MD ? 330 : undefined }}>
                    <VerticalPaginatedList
                        isFirstLoading={isLoading}
                        isForbidden={allLocations === null}
                        isError={allLocations === false}
                        notFound={(allLocations && allLocations.length === 0) ?? false}
                        totalPages={totalPages}
                        pageNum={pageNum}
                        isPreviousData={isPreviousData}
                        isNextDisabled={isNextDisabled}
                        isPreviousDisabled={isPreviousDisabled}
                        fetchNext={fetchNext}
                        fetchPrevious={fetchPrevious}
                    >
                        <div style={{ height: LG && height >= ScreenSize.MD ? 280 : undefined, overflowY: 'auto' }}>
                            {allLocations && allLocations.map(location => {
                                return (
                                    <ServiceLocationSelectItem
                                        key={location.id}
                                        name={location.name}
                                        description={location.description}
                                        image={location.image.path}
                                        isSelected={selectedLocation?.id === location?.id}
                                        isDisabled={currentSelectedLocations.some(chosenLocation => chosenLocation.id === location.id)}
                                        isCurrentLocation={currentLocation?.id === location?.id}
                                        onSelect={() => onSelectLocation(location)}
                                    />
                                )
                            })}
                        </div>
                    </VerticalPaginatedList>
                </div>
                <HorizontalContainer style={{
                    justifyContent: 'flex-end',
                    gap: marginXS
                }}>
                    <Button onClick={onClose}>
                        {__`cancel`}
                    </Button>
                    <Button type='primary' onClick={() => onChange(selectedLocation)}>
                        {__`confirm`}
                    </Button>
                </HorizontalContainer>
            </VerticalContainer>
        </FormSection>
    )
}

type ServiceLocationSelectItemProps = {
    name: string
    description: string | null
    image: string | null
    isSelected: boolean
    isDisabled: boolean
    isCurrentLocation: boolean
    onSelect: () => void
}

const ServiceLocationSelectItem = ({
    name,
    description,
    image,
    isSelected,
    isDisabled,
    isCurrentLocation,
    onSelect
}: ServiceLocationSelectItemProps) => {
    const { token: { paddingXXS, marginXS } } = theme.useToken()
    const { __ } = useTranslate()
    const { SM } = useWindowDimensions()
    
    return (
        <Button
            type='text'
            title={isDisabled && !isCurrentLocation ? __`location_is_already_used` : undefined}
            style={{
                width: '100%',
                height: 'auto',
                padding: `0 ${paddingXXS}px`,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: marginXS,
                borderRadius: SM ? undefined : 0,
                opacity: isDisabled && !isCurrentLocation ? .7 : 1,
                cursor: isDisabled && !isCurrentLocation ? 'not-allowed' : 'pointer'
            }}
            onClick={() => (!isDisabled || isCurrentLocation) && onSelect()}
        >
            <Radio
                checked={isSelected}
                disabled={isDisabled && !isCurrentLocation}
            />
            <Avatar
                name={name}
                tag={isCurrentLocation ? (
                    <>
                        <Divider type='vertical' />
                        <Label
                            type='info'
                            text={__`current`}
                        />
                    </>
                ) : undefined}
                description={description}
                image={image}
                shape='square'
                size='small'
            />
        </Button>
    )
}
